import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import {
  Video,
  BookOpen,
  Upload,
  Settings,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const OnboardingDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: Sparkles,
      title: "Bienvenue sur Valnexo Academy !",
      description: "Créez des formations professionnelles en quelques clics. Suivez ce guide pour découvrir les fonctionnalités principales.",
      action: null
    },
    {
      icon: Video,
      title: "Enregistrez vos vidéos",
      description: "Le Studio vous permet de capturer votre écran et votre caméra simultanément. Idéal pour les tutoriels et présentations.",
      action: { label: "Voir le Studio", path: "/studio" }
    },
    {
      icon: BookOpen,
      title: "Organisez vos formations",
      description: "Structurez vos contenus en modules et leçons. Définissez l'ordre de progression pour vos apprenants.",
      action: { label: "Créer une formation", path: "/trainings/new" }
    },
    {
      icon: Upload,
      title: "Publiez sur YouTube",
      description: "Uploadez vos vidéos directement sur YouTube en mode non répertorié. Les liens sont automatiquement récupérés.",
      action: { label: "Voir la bibliothèque", path: "/media" }
    },
    {
      icon: Settings,
      title: "Personnalisez votre marque",
      description: "Ajoutez votre logo, vos couleurs et polices pour que vos formations reflètent votre identité.",
      action: { label: "Configurer", path: "/settings" }
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as complete
      localStorage.setItem('valnexo_onboarding_complete', 'true');
      onOpenChange(false);
    }
  };

  const handleAction = (path) => {
    localStorage.setItem('valnexo_onboarding_complete', 'true');
    onOpenChange(false);
    navigate(path);
  };

  const handleSkip = () => {
    localStorage.setItem('valnexo_onboarding_complete', 'true');
    onOpenChange(false);
  };

  const step = steps[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <Icon className="w-8 h-8 text-blue-600" />
          </div>
          <DialogTitle className="text-xl font-semibold font-['Outfit']">
            {step.title}
          </DialogTitle>
          <DialogDescription className="text-slate-500 mt-2">
            {step.description}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="py-4">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
            <span>Étape {currentStep + 1} sur {steps.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 py-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentStep 
                  ? 'bg-blue-600' 
                  : i < currentStep 
                  ? 'bg-blue-300' 
                  : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-slate-500"
          >
            Passer
          </Button>
          
          <div className="flex gap-2 flex-1 justify-end">
            {step.action && (
              <Button
                variant="outline"
                onClick={() => handleAction(step.action.path)}
              >
                {step.action.label}
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {currentStep === steps.length - 1 ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Terminer
                </>
              ) : (
                <>
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Check if onboarding should be shown
    const onboardingComplete = localStorage.getItem('valnexo_onboarding_complete');
    if (user && !onboardingComplete) {
      // Small delay to let the dashboard load first
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  return {
    showOnboarding,
    setShowOnboarding,
    OnboardingDialog
  };
};

export default OnboardingDialog;
